import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getAuth, fetchProfileAndCompletionStatus } from '../api';
import FloatingChatWidget from './FloatingChatWidget';

/**
 * Route guard for any page that requires a fully-signed-up, fully-onboarded
 * user.
 *
 * Outcomes:
 *   1. No auth                   → "/"          (landing / login)
 *   2. Auth + profile incomplete → "/register"  (resume onboarding)
 *   3. Auth + complete           → render the route
 *
 * The AI intro screen lives at /ai-intro inside this guard. Reaching it
 * is the responsibility of whoever is doing the navigating — RegisterPage
 * sends the user there once the final profile PUT succeeds. Earlier
 * versions tried to enforce "show the intro once" via a localStorage flag
 * here, which broke when more than one account ever signed in on the
 * same browser: the flag from account A would suppress the intro for
 * account B. Cleanest fix is to take this guard out of that decision —
 * /ai-intro is just a normal authed route now, and the register flow
 * decides when to visit it.
 *
 * The FloatingChatWidget is mounted next to <Outlet /> so it appears on
 * every authenticated page automatically (Dashboard, Activities, etc.).
 * It is hidden on /ai-intro because the intro screen IS the duck —
 * having a floating duck bubble on top of it would be redundant.
 */
export default function ProtectedRoute() {
  // 'loading' | 'unauth' | 'incomplete' | 'complete'
  const [status, setStatus] = useState('loading');
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const auth = getAuth();
      if (!auth) {
        if (!cancelled) setStatus('unauth');
        return;
      }
      const result = await fetchProfileAndCompletionStatus();
      if (cancelled) return;
      if (!result) {
        // Token rejected — wipe the slate so they can sign back in.
        setStatus('unauth');
        return;
      }
      setStatus(result.profileComplete ? 'complete' : 'incomplete');
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === 'loading') return <LoadingScreen />;
  if (status === 'unauth') return <Navigate to="/" replace />;
  if (status === 'incomplete') return <Navigate to="/register" replace />;

  const onIntroPage = location.pathname === '/ai-intro';

  return (
    <>
      <Outlet />
      {!onIntroPage && <FloatingChatWidget />}
    </>
  );
}

function LoadingScreen() {
  return (
    <div style={loadingStyles.root}>
      <div style={loadingStyles.spinner} />
    </div>
  );
}

const loadingStyles = {
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #c2f3ef 0%, #57c0b2 30%, #94e4c9 100%)',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255, 255, 255, 0.4)',
    borderTopColor: '#FFFFFF',
    borderRadius: '50%',
    animation: 'protected-spin 0.9s linear infinite',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('protected-route-keyframes')) {
  const style = document.createElement('style');
  style.id = 'protected-route-keyframes';
  style.textContent = `@keyframes protected-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}